/**
 * Partner directory with activity buckets and coverage at a glance.
 *
 * The directory opens on the active partner units with staff in them — alumni are past
 * partners, inactive ones have stepped back, and a unit the registry counts as non-staff
 * partners only is not what the page is a directory of. Those are two filters set rather
 * than two hidden rules (see `OPENS_ON`), so they show in the filter count, say so in the
 * header, and clear like anything else typed in the panel.
 *
 * Two ways to read the same rows: cards, which put a face and a description beside the
 * figures, and the table, which is the directory as a register. It opens on the cards —
 * these are people, and the register is the specialised reading, wanted when you are
 * scanning dates or counts down a column. Both are fed by one query, so switching
 * between them costs nothing and never changes the result.
 *
 * THE FAVORITES PAGE IS THIS PAGE. `onlyFavorites` adds one filter the reader cannot
 * clear and changes the words at the top; everything else — the filters, the sort, the
 * paging, both views, the CSV — is the same code, because the ask was for the Partner
 * Units view narrowed to a reader's own picks rather than for a second directory that
 * would drift from this one the first time either was touched.
 *
 * SO ARE TRAINING SCHOOLS AND PROJECTS. `fundType` is the same trick a second time, and
 * for the same reason: the registry's three kinds differ by one column (see migration
 * 0031), so three directories would be three copies of this file drifting apart — the day
 * somebody added a filter to one of them, the other two would be missing it. One page,
 * one pinned filter, three entries in the sidebar.
 *
 * `fundType` and `onlyFavorites` do not combine, and that is deliberate: Favorites is
 * whatever a reader has starred, of any kind. A page pinned to one kind would be a
 * shortlist that silently hid part of itself.
 */
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader, useNavCount } from '../App'
import { useApi, qs, fetchAllRows } from '../lib/api'
import { CountrySelect, EnumSelect } from '../components/Select'
import { PagedTable, PagedCards, ViewToggle, useViewMode } from '../components/DataTable'
import { MediaImage } from '../components/ImageUpload'
import {
  Field, Badge, Empty, AlumniToggle, SearchInput,
  ActivityBadge, downloadCsv, Filters, FilterActions, countFilters, fmtInt, headcount, relDays, CountryLabel,
  useToast,
} from '../components/ui'
import { useCanBrowse } from '../lib/viewmode'
import FavoriteButton from '../components/Favorite'
import { useRelabel, useCode } from '../lib/terms'
import { fundTypeLabel } from '../../shared/fundTypes.js'
import { Icon } from '../components/Icon'

/**
 * What each of the three Register pages is called, and what one of its rows is called.
 *
 * `count` is the sidebar badge's key: three keys rather than one, so that a filter typed
 * on the Projects page moves the Projects badge and leaves the other two alone.
 *
 * Only `staff` goes through the relabeller. An installation that calls its units something
 * other than partner units is renaming *its own people*; a training school and a project
 * are what they say they are, and running them through the same setting would rename them
 * too the moment somebody set it.
 */
const KINDS = {
  staff: { title: 'Partner Units', one: 'partner unit', many: 'partner units', count: 'partners', relabel: true },
  training: {
    title: 'Training Schools',
    one: 'training school',
    many: 'training schools',
    count: 'trainingSchools',
    relabel: false,
  },
  project: { title: 'Projects', one: 'project', many: 'projects', count: 'projects', relabel: false },
}

/**
 * The number of updates written about a unit — a link to the report that lists them, for
 * an account that has that report, and the number alone for one that does not. Written
 * once because the table and the cards both show it, and they would otherwise disagree
 * about it on the day one of them was changed.
 */
/**
 * The kind of record, where the page is showing more than one kind.
 *
 * `show` is false on the three Register pages, where every row is the same kind and a
 * badge on all of them says nothing — and true on Favorites, which is whatever a reader
 * has starred and is the one directory view where the three mix.
 */
const KindBadge = ({ r, show }) =>
  show && r.fund_type && r.fund_type !== 'staff' ? (
    <Badge tone="accent">{fundTypeLabel(r.fund_type)}</Badge>
  ) : null

function UpdateCount({ r }) {
  const canBrowse = useCanBrowse()
  if (!r.newsletter_count) return <span className="subtle">0</span>
  if (!canBrowse) return fmtInt(r.newsletter_count)
  return (
    <Link to={`/reports/newsletters-by-partner?partner=${r.partner_id}`}>
      {fmtInt(r.newsletter_count)}
    </Link>
  )
}

/*
 * No filter at all. What the ✕ and "Clear filters" go back to, and the state every one of
 * these pages would open in if `OPENS_ON` below did not narrow one of them.
 */
const EMPTY = {
  q: '', country_id: [], activity: null, status: null, orphan: null,
  year_from: '', year_to: '', include_alumni: false, staffing: null,
}

/*
 * What the Partner Units page opens on, as filters rather than as a rule.
 *
 * Both of these are set in the panel, count towards the badge on it, are named in the
 * line under the title and clear like anything else typed there — which is the whole
 * point. A directory that quietly leaves records out is a directory you cannot trust the
 * total of; one that opens with two filters showing is answering the question almost
 * everybody has and saying so.
 *
 *   status: 'active'    Alumni are past partner units and the API leaves them out on its
 *                       own. This also drops anything marked inactive — a unit that has
 *                       stepped back — which the alumni rule alone kept. Note that while
 *                       it is set the alumni switch below has nothing to decide and
 *                       disables itself, saying so; clearing the status gives it back.
 *   staffing: 'staff'   The units the registry's head count found staff in. Some are
 *                       counted entirely as non-staff partners, and those are what this
 *                       leaves out — along with the ones nobody has counted yet, which
 *                       is the part worth knowing about: a unit reads uncounted until a
 *                       file carrying the two columns has been imported over it, so if
 *                       this page ever looks short, this is the filter to clear.
 *
 * Only this page. Training Schools and Projects are not counted in staff, so the second
 * would empty them; Favorites is whatever a reader has starred, and a page of their own
 * picks must not hide any of them.
 */
const OPENS_ON = { status: 'active', staffing: 'staff' }

/*
 * The three answers the registry's head count gives about a unit's staff, and what the
 * line under the title says when one of them is picked.
 *
 * Three rather than a switch, because "nobody has counted this unit yet" is its own
 * answer and not the same as "counted, and none of them are staff" — see migration 0026,
 * and STAFFING_CLAUSES in the Worker, which these three words are the other half of.
 */
const STAFFING = [
  { value: 'staff', label: 'Has staff partners', note: 'with staff partners' },
  { value: 'non_staff', label: 'Non-staff partners only', note: 'non-staff partners only' },
  { value: 'uncounted', label: 'Not counted yet', note: 'no head count on file' },
]

/*
 * Every key a link can arrive with. If it names any of them, the page opens on exactly
 * what it names and `OPENS_ON` stays out of it — the Dashboard's boxes point here at the
 * alumni and at the whole registry, and a default that narrowed those links would make
 * each of those figures one you could click and be shown fewer of. It is also the escape
 * hatch for a link that wants the page wide: any value the filter does not recognise —
 * `staffing=all` reads well — turns that default off without turning on a filter.
 */
const FILTER_KEYS = [
  'q', 'country_id', 'activity', 'status', 'orphan', 'year_from', 'year_to',
  'include_alumni', 'staffing',
]

/**
 * Filters can arrive in the URL, so a link from elsewhere can open the directory already
 * narrowed — the dashboard's Partner Units box points at the alumni and at the whole
 * registry. Read once into the initial state, the way the Updates list reads its own:
 * after that the filter panel is the control, and a URL that kept overwriting it would
 * fight the person using it.
 *
 * `status` and `include_alumni` are the two that matter here and they interact, which the
 * Worker settles rather than this: an explicit status wins outright, so `status=alumni`
 * asks for exactly the rows the alumni default would otherwise remove.
 *
 * `opensNarrowed` is the Partner Units page saying it has an opening view — see
 * `OPENS_ON`. A link that names any filter is taken at its word and gets none of it.
 */
function fromUrl(params, opensNarrowed = false) {
  const status = params.get('status')
  const activity = params.get('activity')
  const staffing = params.get('staffing')
  const year = (k) => (/^\d{4}$/.test(params.get(k) ?? '') ? params.get(k) : '')
  const opening = opensNarrowed && !FILTER_KEYS.some((k) => params.has(k)) ? OPENS_ON : {}
  return {
    ...EMPTY,
    q: params.get('q') ?? '',
    country_id: (params.get('country_id') ?? '')
      .split(',')
      .map((s) => Number(s))
      .filter((n) => Number.isInteger(n) && n > 0),
    activity: ['active', 'slowing', 'dormant', 'none'].includes(activity) ? activity : null,
    status: ['active', 'inactive', 'alumni'].includes(status) ? status : null,
    orphan: ['0', '1'].includes(params.get('orphan')) ? params.get('orphan') : null,
    year_from: year('year_from'),
    year_to: year('year_to'),
    include_alumni: params.get('include_alumni') === '1',
    staffing: STAFFING.some((o) => o.value === staffing) ? staffing : null,
    // Last, and only ever on a URL that named no filter at all — so there is nothing
    // above for it to overwrite, and a link that did name one keeps every word of it.
    ...opening,
  }
}

export default function PartnerList({ user, onlyFavorites = false, fundType = null }) {
  // Favorites is every kind, so it reads as the staff wording and pins nothing.
  const kind = KINDS[onlyFavorites ? 'staff' : fundType] ?? KINDS.staff
  const pinned = onlyFavorites ? null : fundType
  // Whether the rows on screen can be of more than one kind. Only Favorites can.
  const mixed = !pinned
  const code = useCode()
  const t = useRelabel()
  const [params] = useSearchParams()
  const [f, setF] = useState(() => fromUrl(params, pinned === 'staff'))
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  /* Partner Units open on the code, because that is how the office refers to one and how
     four hundred of them are looked up. The other two pages open on the country instead:
     they hold a few dozen records each, nobody knows a training school by its number, and
     the question actually being asked of those pages is "what is where". Only the opening
     sort — the column headings still sort all three pages every way. */
  const [sort, setSort] = useState(pinned && pinned !== 'staff' ? 'country_name' : 's4ca_code')
  const [dir, setDir] = useState('asc')
  // Its own remembered view mode and its own remembered filter panel. A reader scanning
  // a dozen units they follow and a reader working down the whole registry are not doing
  // the same thing, and the second should not decide how the first opens.
  const [view, setView] = useViewMode(onlyFavorites ? 'favorites' : pinned ? `partners-${pinned}` : 'partners')
  // Answers a press before the refetch does. The button reports what the database
  // settled on, so this is the truth arriving early rather than a guess.
  const [picked, setPicked] = useState({})

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const toast = useToast()
  const query = useApi(
    ['partners', onlyFavorites, pinned, f, page, limit, sort, dir],
    `partners${qs({
      ...f,
      include_alumni: f.include_alumni ? 1 : null,
      favorites: onlyFavorites ? 1 : null,
      // Not one of `f`: it is what this page *is*, not something typed on it, so it does
      // not count as an applied filter and the panel's ✕ cannot clear it.
      fund_type: pinned,
      page,
      limit,
      sort,
      dir,
    })}`,
    { keepPreviousData: true }
  )

  // Every unit these filters match, in this order — it used to be only the page on screen.
  const exportAll = async () => {
    try {
      const all = await fetchAllRows('partners', {
        ...f,
        include_alumni: f.include_alumni ? 1 : null,
        favorites: onlyFavorites ? 1 : null,
        fund_type: pinned,
        sort,
        dir,
      })
      downloadCsv(csvName, all.rows, [
                { key: 's4ca_code', label: 'S4CA Code' },
                { key: 'display_name', label: 'Name' },
                { key: 'country_name', label: 'Country' },
                // On every page, not just the mixed one: a spreadsheet of the directory
                // outlives the page it came from, and "which kind was this" is not a
                // question the filename can answer once three of them are in a folder.
                { key: 'fund_type', label: 'Fund Type' },
                { key: 'initial_year', label: 'Initial Year' },
                // The two as the file gives them and the total the app shows, so a
                // spreadsheet can add them up its own way.
                { key: 'staff_partners', label: 'Staff' },
                { key: 'non_staff_partners', label: 'Non-Staff Partners' },
                { key: 'partners_in_unit', label: 'Partners' },
                { key: 'newsletter_count', label: 'Updates' },
                { key: 'first_newsletter', label: 'First' },
                { key: 'last_newsletter', label: 'Latest' },
                { key: 'activity', label: 'Activity' },
      ])
      if (all.total > all.rows.length) toast.info(`Exported the first ${fmtInt(all.rows.length)} of ${fmtInt(all.total)} rows`)
    } catch (err) {
      toast.error(err)
    }
  }

  // Keep the sidebar badge on the same number as this table, filters and all. Only the
  // directory does this: the Favorites badge counts everything a reader picked, and a
  // filter typed on that page must not make the sidebar say they picked fewer.
  useNavCount(kind.count, onlyFavorites ? undefined : query.data?.total)

  // 'partner-units.csv', 'training-schools.csv', 'projects.csv' — the page's own name, so
  // three downloads in a folder are three files rather than three copies of partners.csv.
  const csvName = onlyFavorites
    ? 'favorites.csv'
    : `${kind.title.toLowerCase().replace(/\s+/g, '-')}.csv`

  // 'New Partner Unit' goes through the relabeller and the other two do not — see KINDS.
  const newLabel = kind.relabel
    ? t(`New ${kind.title.replace(/s$/, '')}`)
    : `New ${kind.title.replace(/s$/, '')}`

  // Both narrowings say so under the title, the status in its own words and this one in
  // words rather than a value: "· with staff partners" reads as a sentence where
  // "· staff staffing only" would read as a bug.
  const staffingNote = STAFFING.find((o) => o.value === f.staffing)?.note

  const isFav = (r) => (r.partner_id in picked ? picked[r.partner_id] : !!r.is_favorite)
  const onPicked = (partnerId, on) => setPicked((prev) => ({ ...prev, [partnerId]: on }))

  const onSort = (key) => {
    if (sort === key) setDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSort(key)
      setDir(key === 'newsletter_count' || key === 'last_newsletter' ? 'desc' : 'asc')
    }
  }

  const columns = [
    {
      key: 's4ca_code',
      label: 'Code',
      sortKey: 's4ca_code',
      width: 100,
      render: (r) => (
        <Link className="mono" to={`/partners/${r.partner_id}`}>
          {code(r.s4ca_code)}
        </Link>
      ),
    },
    {
      key: 'display_name',
      label: 'Name',
      sortKey: 'display_name',
      render: (r) => (
        <span className="row row-tight">
          <span>{code(r.display_name)}</span>
          <KindBadge r={r} show={mixed} />
          {r.is_orphan ? <Badge tone="warn" title="Not present in the registry file">stub</Badge> : null}
          {r.status !== 'active' ? <Badge>{r.status}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'country_name',
      label: 'Country',
      sortKey: 'country_name',
      render: (r) => <CountryLabel id={r.country_id} name={r.country_name} flag={r.flag_emoji} />,
    },
    { key: 'initial_year', label: 'Since', sortKey: 'initial_year', numeric: true, width: 70, render: (r) => r.initial_year ?? '—' },
    {
      /* How many people the unit is, from the registry file's two head-count columns.
         Beside "Since" rather than among the rollups further along, because both are
         facts the file states about the unit; everything from Updates rightwards is
         something the archive worked out about it. The breakdown is in the title. */
      key: 'partners_in_unit',
      label: 'Partners',
      sortKey: 'partners_in_unit',
      numeric: true,
      width: 86,
      render: (r) => {
        const h = headcount(r)
        return <span title={t(h.title)}>{h.label}</span>
      },
    },
    {
      key: 'newsletter_count',
      label: 'Updates',
      sortKey: 'newsletter_count',
      numeric: true,
      width: 100,
      render: (r) => <UpdateCount r={r} />,
    },
    { key: 'prayer_request_count', label: 'Prayers', numeric: true, width: 78, render: (r) => fmtInt(r.prayer_request_count) },
    { key: 'highlighted_count', label: <Icon name="star" size={13} label="Highlighted" />, numeric: true, width: 54, render: (r) => fmtInt(r.highlighted_count) },
    {
      key: 'last_newsletter',
      label: 'Latest',
      sortKey: 'last_newsletter',
      width: 130,
      render: (r) =>
        r.last_newsletter ? (
          <span className="nowrap">
            <span className="mono small">{r.last_newsletter}</span>
            <div className="tiny subtle">{relDays(r.last_newsletter)}</div>
          </span>
        ) : (
          <span className="subtle">never</span>
        ),
    },
    { key: 'activity', label: 'Activity', sortKey: 'activity', width: 116, render: (r) => <ActivityBadge activity={r.activity} /> },
    {
      key: 'favorite',
      // A heart rather than the word, because the column is 44px and the header of a
      // column of hearts does not need explaining. Named for screen readers instead.
      label: <Icon name="heart" size={13} label="Favorites" />,
      width: 44,
      render: (r) => (
        <FavoriteButton
          partnerId={r.partner_id}
          name={r.display_name}
          on={isFav(r)}
          onChange={onPicked}
        />
      ),
    },
  ]

  return (
    <PageHeader
      title={onlyFavorites ? 'Favorites' : kind.relabel ? t(kind.title) : kind.title}
      sub={
        onlyFavorites
          ? 'The partner units you follow — press the heart on any unit to add it here'
          : !query.data
            ? 'Loading…'
            : /* "S4CA" as a literal, which is how every other authored line in the app
                 writes it: `PageHeader` runs this line through the relabeller, and that is
                 what turns the prefix into an installation's own. It was `${code}` here —
                 the hook returns a *function*, so the line read "in the s=>Io(s,t)
                 registry" on all three Register pages. */
              `${fmtInt(query.data.total)} ${query.data.total === 1 ? kind.one : kind.many} in the S4CA registry` +
              (f.status ? ` · ${f.status} status only` : '') +
              (staffingNote ? ` · ${staffingNote}` : '') +
              (query.data.alumniHidden
                ? ` · ${fmtInt(query.data.alumniHidden)} alumni hidden`
                : '') +
              /* Only the staffed view sends this, and it is worth the words on the day it
                 matters: no unit has a head count until a registry file carrying the two
                 columns has been imported, and until then this filter hides everything.
                 A page reading "0 partner units · 418 with no staff count hidden" sends
                 somebody to the filter; a page reading "0 partner units" sends them to
                 me. */
              (query.data.staffingHidden
                ? ` · ${fmtInt(query.data.staffingHidden)} with no staff count hidden`
                : '')
      }
      actions={
        <>
          <ViewToggle view={view} onView={setView} />
          <button className="btn btn-sm" onClick={exportAll} disabled={!query.data?.total}>
            ↓ CSV
          </button>
          {/* Not on Favorites. That page is a reading of the directory, and a button
              that creates a partner unit from it would create one that is not on it. */}
          {user.caps.create && !onlyFavorites && (
            <Link
              className="btn btn-sm btn-primary"
              // The kind travels with the link, so a record created from the Projects page
              // is a project. Without it the form would default to staff and the new
              // record would not appear on the page it was created from.
              to={`/partners/new${pinned ? `?fund_type=${pinned}` : ''}`}
            >
              + {newLabel}
            </Link>
          )}
        </>
      }
    >
      <div className="col">
        {/* Out of the filter panel and above it, the way the dashboard and Updates Overview
            put their own: searching for one unit is the commonest reason to open this page,
            and it used to be behind a Show. It is still the same `q` filter, so it still
            counts as one applied and the panel's ✕ still clears it.

            It also reaches further than it looks. Besides the code, the name and the
            country, the term is matched against the alias and preferred names the archive
            holds and never shows — so somebody who knows what a unit is really called can
            type that and be given the record. See migration 0028. */}
        <SearchInput
          id="p-q"
          value={f.q}
          onChange={(v) => set({ q: v })}
          placeholder="S4CA-123, a name, or a country…"
          aria-label="Search partner units"
        />
        <Filters id={onlyFavorites ? 'favorites' : 'partners'} active={countFilters(f)} onClear={() => set(EMPTY)}>
          <Field label="Country" htmlFor="p-country">
            <CountrySelect inputId="p-country" multi value={f.country_id} onChange={(v) => set({ country_id: v })} />
          </Field>
          <Field label="Activity" htmlFor="p-activity">
            <EnumSelect
              inputId="p-activity"
              value={f.activity}
              onChange={(v) => set({ activity: v })}
              options={[
                { value: 'active', label: 'Active (within 90 days)' },
                { value: 'slowing', label: 'Slowing (within a year)' },
                { value: 'dormant', label: 'Dormant (over a year)' },
                { value: 'none', label: 'No updates at all' },
              ]}
            />
          </Field>
          <Field label="Status" htmlFor="p-status">
            <EnumSelect inputId="p-status" value={f.status} onChange={(v) => set({ status: v })} options={['active', 'inactive', 'alumni']} />
          </Field>
          {/* Between Status and Registry: the three of them are what the office knows about
              a unit rather than what the archive has watched it do, and this one is read
              off the same registry file the one below asks about. */}
          <Field label="Staffing" htmlFor="p-staffing" hint="From the registry's head count">
            <EnumSelect
              inputId="p-staffing"
              value={f.staffing}
              onChange={(v) => set({ staffing: v })}
              options={STAFFING.map(({ value, label }) => ({ value, label }))}
            />
          </Field>
          <Field label="Registry" htmlFor="p-orphan">
            <EnumSelect
              inputId="p-orphan"
              value={f.orphan}
              onChange={(v) => set({ orphan: v })}
              options={[
                { value: '0', label: 'In the registry file' },
                { value: '1', label: 'Placeholder stubs only' },
              ]}
            />
          </Field>
          <Field label="Unit record since" hint="Initial year range">
            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <input
                className="input"
                type="number"
                placeholder="from"
                value={f.year_from}
                onChange={(e) => set({ year_from: e.target.value })}
              />
              <input
                className="input"
                type="number"
                placeholder="to"
                value={f.year_to}
                onChange={(e) => set({ year_to: e.target.value })}
              />
            </div>
          </Field>
          <AlumniToggle
            checked={f.include_alumni}
            onChange={(v) => set({ include_alumni: v })}
            disabled={!!f.status}
            where="entries"
            hint={
              f.status
                ? 'The Status filter above decides this while it is set.'
                : 'Past partner units, left out of the register by default.'
            }
            title={
              f.status
                ? 'Clear the Status filter to use this toggle'
                : 'Include partner units marked as alumni'
            }
          />
          <FilterActions active={countFilters(f)} onClear={() => set(EMPTY)} busy={query.isFetching} />
        </Filters>

        {view === 'table' ? (
          <PagedTable
            empty={
              onlyFavorites && !countFilters(f) ? (
                <Empty icon="heart-outline" title="You have not chosen any partner units yet">
                  Open Partner Units and press the <Icon name="heart-outline" size={12} /> beside anyone you want to follow.
                  They will be gathered here.
                </Empty>
              ) : undefined
            }
            columns={columns}
            data={query.data}
            loading={query.isLoading}
            error={query.isError ? query.error : null}
            refetch={query.refetch}
            sort={sort}
            dir={dir}
            onSort={onSort}
            rowKey={(r) => r.partner_id}
            rowTo={(r) => `/partners/${r.partner_id}`}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        ) : (
          /*
           * The card leads with the two things a table row has no room for — the photo
           * and the description — so it reads as a profile rather than a record, and
           * keeps beside them only the figures that say how much of this unit the
           * archive holds. The table is still where every column lives; nothing here is
           * the only way to see a number.
           */
          <PagedCards
            data={query.data}
            loading={query.isLoading}
            error={query.isError ? query.error : null}
            refetch={query.refetch}
            columns={columns}
            sort={sort}
            dir={dir}
            onSort={onSort}
            rowKey={(r) => r.partner_id}
            card={(r) => <PartnerCard r={r} favorite={isFav(r)} onFavorite={onPicked} mixed={mixed} />}
            empty={
              onlyFavorites && !countFilters(f) ? (
                /* An empty Favorites page is almost never a filter that matched nothing —
                   it is a reader who has not picked anybody yet, and it should say where
                   the picking is done rather than shrug. */
                <Empty icon="heart-outline" title="You have not chosen anyone to follow yet">
                  Open Partner Units, Training Schools or Projects and press the <Icon name="heart-outline" size={12} /> beside
                  anything you want to follow. It will be gathered here.
                </Empty>
              ) : (
                <Empty icon="person" title={`No matching ${kind.many}`} />
              )
            }
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        )}
      </div>
    </PageHeader>
  )
}

/**
 * One partner unit as a card: who they are on the left, what the archive holds on the
 * right, and the description under both — read across the top, then down.
 *
 * The whole card opens the unit, the way a table row does. The links inside it go where
 * they say instead — the country to its own page, the update count to the report — and
 * a click that ends a text selection is someone copying the description, not asking to
 * navigate. Written as a section rather than a <Card>, since a card holding links
 * cannot itself be one: a link inside a link is not valid HTML.
 */
function PartnerCard({ r, favorite, onFavorite, mixed }) {
  const code = useCode()
  const t = useRelabel()
  const navigate = useNavigate()
  const people = headcount(r)

  const open = (e) => {
    if (e.target.closest('a,button,input,label,select')) return
    if (window.getSelection?.()?.toString()) return
    navigate(`/partners/${r.partner_id}`)
  }

  return (
    <section className="card pu-card clickable" onClick={open}>
      {/* Top right, out of the flow, because it belongs to the whole card rather than to
          any row inside it — and because putting it in the small-things row below moved
          the Donate button every time a name was a line longer. */}
      <div className="pu-fav">
        <FavoriteButton
          partnerId={r.partner_id}
          name={r.display_name}
          on={favorite}
          onChange={onFavorite}
          size=""
        />
      </div>
      <div className="card-body">
        <div className="pu-top">
          <div className="pu-head">
            {r.photo_media_id ? (
              <MediaImage id={r.photo_media_id} alt={r.display_name} className="pu-photo" />
            ) : (
              <div className="pu-photo pu-photo-empty" aria-hidden="true">
                <Icon name="person" size={40} />
              </div>
            )}
            <div className="col-tight">
              {/* The code, and beside it how many people the unit is.
                  
                  It sits here rather than in the row of small things below because that
                  row already carries the year, the badges a record earns and the Donate
                  button, and on an alumni unit at a middling window width one more item
                  wrapped it onto a second line — which put back the height this was
                  moved out of the figures column to save. This line holds a nine-character
                  code and nothing else, so there is room on every card at every width.
                  
                  A glyph and a number, the same idiom as the dashboard's compact cards,
                  with the sentence in the title and in the accessible name. Nothing at all
                  when nobody has been counted: an em dash earns its place in a labelled
                  column, where the label asks the question the dash answers, but in a run
                  of small print it is a stray mark. */}
              <div className="pu-ident">
                <Link className="mono small" to={`/partners/${r.partner_id}`}>
                  {code(r.s4ca_code)}
                </Link>
                {people.total !== null ? (
                  <span
                    className="tiny subtle nowrap"
                    title={t(people.title)}
                    aria-label={t(`${people.label} ${people.total === 1 ? 'partner' : 'partners'} in this unit`)}
                  >
                    <Icon name="person" size={12} /> {people.label}
                  </span>
                ) : null}
              </div>
              <Link className="pu-name truncate" to={`/partners/${r.partner_id}`}>
                {code(r.display_name)}
              </Link>
              <div className="small truncate">
                <CountryLabel id={r.country_id} name={r.country_name} flag={r.flag_emoji} />
              </div>
              {/* One row for everything small: the year, whatever the record has to
                  own up to, and the giving link. They used to be two lines with the
                  badges appearing only sometimes, which left the card a line taller
                  for some units than others; together they take the height of the
                  tallest of them, which is the button, and the card stays the size it
                  was.

                  The activity bucket is deliberately not here. It is a reading of the
                  dates, and the date it reads is in the next column — on a card it only
                  repeats itself. */}
              <div className="pu-meta">
                <span className="tiny subtle">
                  {r.initial_year ? `Since ${r.initial_year}` : 'Since not recorded'}
                </span>
                <KindBadge r={r} show={mixed} />
                {r.is_orphan ? <Badge tone="warn" title="Not present in the registry file">stub</Badge> : null}
                {r.status !== 'active' ? <Badge>{r.status}</Badge> : null}
                {r.donate_url ? (
                  <a
                    className="btn btn-donate btn-donate-xs"
                    href={r.donate_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Give to ${r.display_name} — opens ${r.donate_url.replace(/^https?:\/\//, '')}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Donate
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          {/* Two figures, and it has to stay two: this column is what sets the height of
              everything above the description, and the photo beside it is 96px. A third
              entry here made every card in the directory 53px taller — see the head count
              in the row beside the name, which is where a fact the file states about a
              unit belongs anyway. */}
          <dl className="pu-stats">
            <div>
              <dt>Updates</dt>
              <dd>
                <UpdateCount r={r} />
              </dd>
            </div>
            <div>
              <dt>Latest update</dt>
              <dd>
                {r.last_newsletter ? (
                  <>
                    <span className="mono nowrap">{r.last_newsletter}</span>
                    <div className="tiny subtle">{relDays(r.last_newsletter)}</div>
                  </>
                ) : (
                  <span className="subtle">never</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        <p className="pu-desc small muted">
          {r.description || <span className="subtle">No description recorded.</span>}
        </p>
      </div>
    </section>
  )
}
